/**
 * DetailPanel.js
 * * Replaces legacy MainRight.js as the modern Display Shell.
 * Uses Slot-Based Rendering to inject atomic components based on activeSection.
 */

import React, { useState, useEffect, useRef } from 'react';
import { ProcessingVisual } from './ProcessingVisual';
import { CertVisual } from './CertVisual';
import { OriginVisual } from './OriginVisual'; // Keep this for Hero Zone
import { SpeciesVisual } from './SpeciesVisual';
import { UltrawhtVisual } from './UltrawhtVisual';
import { CleanlinessVisual } from './CleanlinessVisual';
import { TreatmentVisual } from './TreatmentVisual';
import { ContentVisual } from './ContentVisual';
import { CMVisual } from './CMVisual';
import { CareVisual } from './CareVisual';
import { SustainabilityVisual } from './SustainabilityVisual.js';
import { FPVisual } from './FPVisual.js';
import { calculateImpact } from '../utils/impact.utils.js'; // Import calculateImpact

import { DetailActiveLeft } from './DetailActiveLeft.js';

import { DetailActiveRight } from './DetailActiveRight.js';

// Image components rendered outside active-desc
import { FillPowerRightImage } from './FillPowerRightImage.js';
import { SpeciesRightImage } from './SpeciesRightImage.js';
import { OriginRightImage } from './OriginRightImage.js';
import { CleanlinessRightImage } from './CleanlinessRightImage.js';
import { FillPowerLeftImage } from './FillPowerLeftImage.js';
import { SpeciesLeftImage } from './SpeciesLeftImage.js';
import { OriginLeftImage } from './OriginLeftImage.js';
import { CleanlinessLeftImage } from './CleanlinessLeftImage.js';
import { ContentLeftImage } from './ContentLeftImage.js';
import { ContentRightImage } from './ContentRightImage.js';
import { BespokeVisual } from './BespokeVisual.js'; // <-- RENAME
import { BespokeLeft } from './BespokeLeft.js';
import { BespokeRight } from './BespokeRight.js';

import { TreatmentLeftImage } from './TreatmentLeftImage.js';
import { TreatmentRightImage } from './TreatmentRightImage.js';
import { ProcessingLeftImage } from './ProcessingLeftImage.js'; // <-- ADD THIS
import { ProcessingRightImage } from './ProcessingRightImage.js'; // <-- ADD THIS
import { CertLeftImage } from './CertLeftImage.js'; // <-- ADD THIS
import { CertRightImage } from './CertRightImage.js'; // <-- ADD THIS
import { CareLeftImage } from './CareLeftImage.js';
import { CareRightImage } from './CareRightImage.js';
import { ImpactUpperText } from './ImpactUpperText.js';

// New Imports from MainRight logic
import { IlPartner } from './IlPartner.js';
import { LotTreatmentBanner } from './LotTreatmentBanner.js';
import { LotFeatureBanner } from './LotFeatureBanner.js';
import ContentSelector from './ContentSelector.js'; // Added ContentSelector import
import { isRenu, fpChecker, triggerSectionTransition, getBespokeConfig } from '../utils/ui.utils'; // Import getBespokeConfig
import { bespokeUpperConfig, bespokeLeftConfig, bespokeRightConfig } from '../config/bespokeBackgrounds'; // <-- ADD THIS
import { IS_LOCALHOST, SUSTAINABILITY_DEV_MODE as DEV_MODE } from '../utils/url.utils';

export const DetailPanel = ({ data, activeSection, t, changeWord, showImpact, openDialog, hideCalculator }) => {
  if (!data) {
    return null;
  }

  const prevActiveSection = useRef();

  // A/B Test for Image Assets (Logic moved from MainRight for parity)
  const [imageCat] = useState(() => {
    if (typeof sessionStorage !== 'undefined') {
      let z = sessionStorage.getItem("counter") || "0";
      // Note: We don't increment here because MainRight or App.js likely already did
      return (Number(z) % 2 === 0) ? "b" : "a";
    }
    return "a";
  });

  // State to manage dynamic classes for the main container
  const [displayClasses, setDisplayClasses] = useState("main-right");

  // Effect to apply classes when data and activeSection are available
  useEffect(() => {
    const effectiveActiveSection = activeSection || "LOT";
    setDisplayClasses(`main-right ${effectiveActiveSection} ${imageCat}`);
  }, [data, activeSection, imageCat]);

  React.useEffect(() => {
    if (prevActiveSection.current !== activeSection && prevActiveSection.current !== undefined) {
      triggerSectionTransition(activeSection);
    }
    prevActiveSection.current = activeSection;
  }, [activeSection]);

  // Brand Logo Logic (Ported from MainRight)
  let brandPrefix = data.prefix;
  let brandPrefixAlt = data.brandPrefixAlt;
  let linky;
  let brandNamePrint;
  let brandLogoPrint;

  if (data.brandNameAlt !== undefined) {
    brandNamePrint = data.brandNameAlt;
    linky = "//trackmydown.com/img/_brands/" + brandPrefixAlt + ".svg";
  } else {
    brandNamePrint = data.brandName;
    linky = "//trackmydown.com/img/_brands/" + brandPrefix + ".svg";
  }

  if (data.brandLogo) {
    if (data.brandLogoAlt !== undefined) {
      brandLogoPrint = data.brandLogoAlt;
    } else {
      brandLogoPrint = true;
    }
  } else {
    brandLogoPrint = false;
  }

  const impactData = React.useMemo(() => {
    return calculateImpact(data);
  }, [data.facility, data.origin, data.down_type, data.verified_fp, data.treatment]);
  
  useEffect(() => {
    const bespokeConfig = getBespokeConfig(
      data.prefix,
      bespokeUpperConfig,
      data
    );
  
    if (bespokeConfig) {
      document.body.dataset.brand = data.prefix;
    } else {
      delete document.body.dataset.bespoke;
    }
  
    return () => {
      delete document.body.dataset.bespoke;
    };
  }, [data]);

  return (
    <aside className={displayClasses}
      data-component="DetailPanel"
      data-brand={data.prefix}
      data-active={activeSection}
      data-rds={!!data.rds}
      data-rsd={!!data.rsd}
      data-grs={data.down_type ? isRenu(data.down_type).toString() : "false"}
      data-fp={data.down_type ? (isRenu(data.down_type) ? fpChecker(data.verified_fp) : "false") : "false"}
      data-treatment={data.normalizedTreatment ? data.normalizedTreatment : "false"}
      data-envimp={showImpact.toString()}
    >
      <div className="upper">
        {/* LOT Section Banner Logic */}
        {activeSection === "LOT" && (
          data.treatment === "ExpeDRY"
            ? <LotFeatureBanner lot={data.display_lot} brand={data.prefix} /> 
            : <LotTreatmentBanner lot={data.display_lot} tr={data.normalizedTreatment} />
        )}

        {(() => {
          const bespokeConfig = getBespokeConfig(data.prefix, bespokeUpperConfig, data);
          if (bespokeConfig && activeSection === "LOT") {
            return (
              <BespokeVisual 
                brand={data.prefix} 
                grs={data.grs || (data.down_type ? isRenu(data.down_type).toString() : undefined)} 
                treatment={data.normalizedTreatment} 
                data_email={data.email} 
                data_math={data.math}
                data={data}
              />
            );
          } else {
            return (<div className="il upper-bg"></div>);
          }
        })()}

        {/* Hero Zone Conditional Rendering */}
        {(activeSection === "FP" || activeSection === "FP2") && (
          <FPVisual />
        )}
        {activeSection === "ORIGIN" && (
          <OriginVisual 
            origin={data.origin ? data.origin[0] : ""} 
            down_type={data.down_type} 
            originExceptionFlag={data.originExceptionFlag} 
          />
        )}
        {activeSection === "SPECIES" && (
          <SpeciesVisual down_type={data.down_type} origin={data.origin ? data.origin[0] : ""} />
        )}
        {activeSection === "CLEANLINESS" && <CleanlinessVisual />}
        {activeSection === "CONTENT" && <ContentVisual />}
        {activeSection === "PROCESSING" && (
          <ProcessingVisual origin={data.origin ? data.origin[0] : ""} />
        )}
        {activeSection === "CERTIFICATIONS" && (
          <CertVisual isGRS={data.down_type ? isRenu(data.down_type) : false} />
        )}        
        {activeSection === "ULTRAWHT" && <UltrawhtVisual />}        
        {/* Hero Zone Conditional Rendering for TREATMENT */}
        {activeSection === "TREATMENT" && data.treatment === "ExpeDRY" ? (
          <LotTreatmentBanner lot={data.display_lot} tr={data.normalizedTreatment} brand={data.prefix} />
        ) : activeSection === "TREATMENT" && (
          <TreatmentVisual tr={data.normalizedTreatment} />
        )}        
        {activeSection === "CM" && <CMVisual />}
        {activeSection === "SUSTAINABILITY" && (
          <>
            <SustainabilityVisual />
            {showImpact && (
              <ImpactUpperText 
                DEV_MODE={DEV_MODE} 
                showImpact={showImpact} 
                t={t} 
                openDialog={openDialog} 
                hideCalculator={hideCalculator}
              />
            )}
          </>
        )}
        {activeSection === "CARE" && <CareVisual />}
      </div>

      <div className="lower">
        <div className="lower-left">
          {/* Permanent Partner Branding */}
          {data.treatment !== "ExpeDRY" ? (
            <IlPartner lot={data.display_lot} tr={data.treatment ? data.treatment[0] : null} />
          ) : null}

          <div className="active-lot">
            <h3>{data.display_lotAlt || data.display_lot}</h3>
          </div>

          <DetailActiveLeft 
            activeSection={activeSection} 
            data={data} 
            changeWord={changeWord} 
            t={t} 
            DEV_MODE={DEV_MODE} 
            showImpact={showImpact} 
            impactData={impactData} // Pass impactData
          />

          {getBespokeConfig(data.prefix, bespokeLeftConfig, data) ? (
            <BespokeLeft 
              brand={data.prefix}
              grs={data.grs || (data.down_type ? isRenu(data.down_type).toString() : undefined)} 
              treatment={data.normalizedTreatment} // Use normalizedTreatment here
              data_email={data.email} 
              data_math={data.math}
              data={data}
            />
          ) : (
            <div className="il lower-bg"></div>
          )}

          {(activeSection === "FP" || activeSection === "FP2") && <FillPowerLeftImage />}
          {activeSection === "SPECIES" && <SpeciesLeftImage down_type={data.down_type} origin={data.origin ? data.origin[0] : ""} />}
          {activeSection === "ORIGIN" && (
            <OriginLeftImage 
              origin={data.origin ? data.origin[0] : ""} 
              originExceptionFlag={data.originExceptionFlag} 
            />
          )}
          {activeSection === "CLEANLINESS" && <CleanlinessLeftImage />}
          {activeSection === "CONTENT" && <ContentLeftImage />}
          {activeSection === "TREATMENT" && <TreatmentLeftImage tr={data.normalizedTreatment} />}
          {activeSection === "PROCESSING" && <ProcessingLeftImage normalizedFacility={data.normalizedFacility} />}
          {activeSection === "CERTIFICATIONS" && <CertLeftImage />}
          {activeSection === "CM" && <CMVisual lower="left" />}
          {activeSection === "CARE" && <CareLeftImage />}
          {activeSection === "SUSTAINABILITY" && <SustainabilityVisual lower="left" />}
        </div>

        <div className="lower-right">
          <small className="lower-credit"></small>

          <DetailActiveRight 
            activeSection={activeSection} 
            data={data} 
            t={t} 
            DEV_MODE={DEV_MODE} 
            showImpact={showImpact} 
            normalizedFacility={data.normalizedFacility}
            impactData={impactData} // Pass impactData
            hideCalculator={hideCalculator} // Pass hideCalculator to DetailActiveRight
          />

          {getBespokeConfig(data.prefix, bespokeRightConfig, data) ? (
            <BespokeRight 
              brand={data.prefix}
              grs={data.grs || (data.down_type ? isRenu(data.down_type).toString() : undefined)} 
              treatment={data.normalizedTreatment} // Use normalizedTreatment here
              data_email={data.email} 
              data_math={data.math}
              data={data}
            />
          ) : (
            <div className="il lower-bg"></div>
          )}

          {(activeSection === "FP" || activeSection === "FP2") && <FillPowerRightImage />}
          {activeSection === "SPECIES" && <SpeciesRightImage down_type={data.down_type} origin={data.origin ? data.origin[0] : ""} t={t} />}
          {activeSection === "ORIGIN" && (
            <OriginRightImage 
              down_type={data.down_type} 
              origin={data.origin ? data.origin[0] : ""} 
              t={t} 
              originExceptionFlag={data.originExceptionFlag} 
            />
          )}
          {activeSection === "CLEANLINESS" && <CleanlinessRightImage />}
          {activeSection === "CONTENT" && <ContentRightImage />}
          {activeSection === "TREATMENT" && <TreatmentRightImage tr={data.normalizedTreatment} />}
          {activeSection === "PROCESSING" && <ProcessingRightImage normalizedFacility={data.normalizedFacility} />}
          {activeSection === "CERTIFICATIONS" && <CertRightImage treatment={data.normalizedTreatment} />}
          {activeSection === "CM" && <CMVisual lower="right" />}
          {activeSection === "CARE" && <CareRightImage />}
          {activeSection === "SUSTAINABILITY" && <SustainabilityVisual lower="right" />}
        </div>
      </div>
    </aside>
  );
};